import z from 'zod';
import { DeepPartial } from '../../common';
import { 
  IFieldErrors, 
  ValidationError, 
  textPattern as namePattern, 
  textPatternMessage as namePatternMessage, 
  textAreaPattern, 
  textAreaPatternMessage,
  requiredTextField,
  optionalTextField
} from 'src/types/validation';
import { DateSchema, EmailSchema, emailPattern, emailPatternMessage,UrlWithTLDSchema,RequiredUrlWithTLDSchema, } from '../../validation';
import { MaterialSchema } from '../../file';
import { getAddressSchema } from '../../address';
import { IAIData, IOrg, IAIAppForm, IAI } from './schoolInterface';
import { getExamSecuritySchema } from './examSecuritySchema';

// Schema for ITestPrep
const TestPrepSchema = z.object({
  description: z.string()
    .min(1, 'Test prep description is required')
    .max(1000, 'Cannot exceed 1000 characters')
    .regex(textAreaPattern, textAreaPatternMessage),
  allowOutsideStudents: z.boolean(),
  hasAdditionalCompensation: z.boolean()
});

// Schema for IStaff
const StaffSchema = z.object({
  count: z.number().nonnegative('Staff count must be non-negative').optional(),
  hasBackgroundChecks: z.boolean().optional(),
  noBackgroundCheckReason: z.string()
    .max(1000, 'Cannot exceed 1000 characters')
    .regex(textAreaPattern, textAreaPatternMessage)
    .optional()
});



const OnsiteStudentsSchema = z.object({
  zeroTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  oneTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  twoTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  threeTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  fourTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  fiveTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  sixTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  sevenTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  eightTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  nineTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  tenTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  elevenTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),

  twelveTaught: z.string()
    .min(1, 'Required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num >= 0 && num <= 9999;
    }, 'Must be a number between 0 and 9999'),
});

const EnrolledStudentsSchema = z.object({
  zeroEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  oneEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  twoEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  threeEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  fourEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  fiveEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  sixEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  sevenEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  eightEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  nineEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  tenEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  elevenEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),

  twelveEnrolled: z.string().min(1, 'Required').refine(val => {
    const num = parseInt(val);
    return !isNaN(num) && num >= 0 && num <= 9999;
  }, 'Must be a number between 0 and 9999'),
});

const ExamsInformationSchema = z.object({
  interestedInAPCourses: z.boolean(),
  apCoursesOffered: z.array(z.string()).optional(),
  preAPCoursesOffered: z.array(z.string()).optional(),
  confirmAPGradeLevels: z.boolean().optional(),
  interestedInPreAPCourses: z.boolean(),
  interestedInPSATSuite: z.boolean(),
  interestedInSAT: z.boolean(),
  satAdministrationTypes: z.array(z.string()).optional(),
  earliestAdministrationDate: z.string().optional()
}).refine(
  (data) => {
    // AP Courses validation
    if (data.interestedInAPCourses === true) {
      return data.apCoursesOffered && data.apCoursesOffered.length > 0;
    }
    return true;
  },
  {
    message: 'At least one AP course must be selected',
    path: ['apCoursesOffered']
  }
).refine(
  (data) => {
    // AP Grade Levels confirmation
    if (data.interestedInAPCourses === true) {
      return data.confirmAPGradeLevels === true;
    }
    return true;
  },
  {
    message: 'You must confirm AP grade levels policy',
    path: ['confirmAPGradeLevels']
  }
).refine(
  (data) => {
    // Pre-AP Courses validation
    if (data.interestedInPreAPCourses === true) {
      return data.preAPCoursesOffered && data.preAPCoursesOffered.length > 0;
    }
    return true;
  },
  {
    message: 'At least one Pre-AP course must be selected',
    path: ['preAPCoursesOffered']
  }
).refine(
  (data) => {
    // SAT Administration Types validation
    if (data.interestedInSAT === true) {
      return data.satAdministrationTypes && data.satAdministrationTypes.length > 0;
    }
    return true;
  },
  {
    message: 'At least one administration type must be selected',
    path: ['satAdministrationTypes']
  }
).refine(
  (data) => {
  
    // SAT Earliest Date validation - only required when Weekend is selected
    if (data.interestedInSAT === true && data.satAdministrationTypes?.includes('Weekend')) {
      return data.earliestAdministrationDate && data.earliestAdministrationDate.trim().length > 0;
    }
    return true;
  },
  {
    message: 'Earliest administration date is required',
    path: ['earliestAdministrationDate']
  }
);

// Schema for IAccreditation
const AccreditationSchema = z.object({
  hasAccreditation: z.boolean(),
  accreditationDetails: z.string()
    .max(1000, 'Cannot exceed 1000 characters')
    .regex(textAreaPattern, textAreaPatternMessage)
    .optional(),
  materials: z.array(MaterialSchema).optional()
}).refine(
  (data) => {
    if (data.hasAccreditation === true) {
      return data.accreditationDetails && data.accreditationDetails.trim().length > 0;
    }
    return true;
  },
  {
    message: 'Accreditation details are required when accreditation is selected',
    path: ['accreditationDetails']
  }
);

// Schema for IStudentSchedule
const DayScheduleItemSchema = z.object({
  startTime: z.string().optional(),
  endTime: z.string().optional()
});

// Create reusable validator function
const hourDecimalValidator = (val: string | undefined) => {
  if (!val || val === '') return true;
  const num = parseFloat(val);
  return !isNaN(num) && num >= 0 && num <= 24 && val.length <= 5;
};

const HoursOnsiteSchema = z.object({
  monday: z.string().optional().refine(
    hourDecimalValidator,
    'Must be a number between 0 and 24 (e.g., 6.25 for 6.25 hours)'
  ),
  tuesday: z.string().optional().refine(
    hourDecimalValidator,
    'Must be a number between 0 and 24 (e.g., 6.25 for 6.25 hours)'
  ),
  wednesday: z.string().optional().refine(
    hourDecimalValidator,
    'Must be a number between 0 and 24 (e.g., 6.25 for 6.25 hours)'
  ),
  thursday: z.string().optional().refine(
    hourDecimalValidator,
    'Must be a number between 0 and 24 (e.g., 6.25 for 6.25 hours)'
  ),
  friday: z.string().optional().refine(
    hourDecimalValidator,
    'Must be a number between 0 and 24 (e.g., 6.25 for 6.25 hours)'
  ),
  saturday: z.string().optional().refine(
    hourDecimalValidator,
    'Must be a number between 0 and 24 (e.g., 6.25 for 6.25 hours)'
  ),
  sunday: z.string().optional().refine(
    hourDecimalValidator,
    'Must be a number between 0 and 24 (e.g., 6.25 for 6.25 hours)'
  )
});

// Convert 12-hour time string (e.g., "8:00 AM") to minutes from midnight
const timeToMinutes = (time12h: string): number | null => {
  const match = time12h?.match(/(\d+):(\d+)\s*(AM|PM)/i);
  if (!match) return null;
  let hours = parseInt(match[1], 10);
  const minutes = parseInt(match[2], 10);
  const period = match[3].toUpperCase();
  if (period === 'AM' && hours === 12) hours = 0;
  else if (period === 'PM' && hours !== 12) hours += 12;
  return hours * 60 + minutes;
};

const StudentScheduleSchema = z.object({
  instructionDays: z.array(z.string()).min(1, 'At least one instruction day must be selected'),
  daySchedule: z.object({
    sunday: DayScheduleItemSchema.optional(),
    monday: DayScheduleItemSchema.optional(),
    tuesday: DayScheduleItemSchema.optional(),
    wednesday: DayScheduleItemSchema.optional(),
    thursday: DayScheduleItemSchema.optional(),
    friday: DayScheduleItemSchema.optional(),
    saturday: DayScheduleItemSchema.optional()
  }).optional(),
  hoursOnsite: HoursOnsiteSchema.optional()
}).refine(
  (data) => {
    // Check if any days are selected and have valid schedules
    const selectedDays = (data.instructionDays || []).filter(day =>
      ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].includes(day.toLowerCase())
    );

    if (selectedDays.length > 0) {
      // Validate that selected days have both start and end times
      for (const day of selectedDays) {
        const daySchedule = data.daySchedule?.[day.toLowerCase() as keyof typeof data.daySchedule];
        if (!daySchedule?.startTime || !daySchedule?.endTime) {
          return false;
        }
      }
    }
    return true;
  },
  {
    message: 'Start and end times are required for all selected days',
    path: ['daySchedule']
  }
).refine(
  (data) => {
    // Check if any days are selected and have hours onsite values
    const selectedDays = (data.instructionDays || []).filter(day =>
      ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].includes(day.toLowerCase())
    );

    if (selectedDays.length > 0) {
      // Validate that selected days have hours onsite entered
      for (const day of selectedDays) {
        const hoursValue = data.hoursOnsite?.[day.toLowerCase() as keyof typeof data.hoursOnsite];
        if (!hoursValue || hoursValue === '') {
          return false;
        }
      }
    }
    return true;
  },
  {
    message: 'Hours on-site are required for all selected days',
    path: ['hoursOnsite']
  }
).superRefine((data, ctx) => {
  const selectedDays = (data.instructionDays || []).filter(day =>
    ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'].includes(day.toLowerCase())
  );
  for (const day of selectedDays) {
    const dayKey = day.toLowerCase();
    const schedule = (data.daySchedule as any)?.[dayKey] as { startTime?: string; endTime?: string } | undefined;
    if (schedule?.startTime && schedule?.endTime) {
      const startMin = timeToMinutes(schedule.startTime);
      const endMin = timeToMinutes(schedule.endTime);
      if (startMin !== null && endMin !== null && endMin <= startMin) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'End Time must be after Start Time',
          path: ['daySchedule', dayKey, 'endTime'],
        });
      }
    }
  }
});

// Schema for IAITestPrep
const AITestPrepSchema = z.object({
  hasTestPrep: z.boolean(),
  description: z.string()
    .max(1000, 'Cannot exceed 1000 characters')
    .regex(textAreaPattern, textAreaPatternMessage)
    .optional(),
  schedule: z.string()
    .max(255, 'Cannot exceed 255 characters')
    .regex(namePattern, namePatternMessage)
    .optional(),
  onlyForEnrolledStudents: z.boolean().optional(),
  hasStudentFees: z.boolean().optional(),
  isOutsideCompany: z.boolean().optional(),
  hasInstructorCompensation: z.boolean().optional(),
  hasAccessToSecureMaterials: z.boolean().optional()
}).refine(
  (data) => {
    if (data.hasTestPrep === true) {
      return data.description && data.description.trim().length > 0;
    }
    return true;
  },
  {
    message: 'Test preparation description is required when test prep services are offered',
    path: ['description']
  }
).refine(
  (data) => {
    if (data.hasTestPrep === true) {
      return data.schedule && data.schedule.trim().length > 0;
    }
    return true;
  },
  {
    message: 'Test prep schedule is required when test prep services are offered',
    path: ['schedule']
  }
).refine(
  (data) => {
    if (data.hasTestPrep === true) {
      return data.onlyForEnrolledStudents !== undefined;
    }
    return true;
  },
  {
    message: 'Please indicate if test prep is only for enrolled students',
    path: ['onlyForEnrolledStudents']
  }
).refine(
  (data) => {
    if (data.hasTestPrep === true) {
      return data.hasStudentFees !== undefined;
    }
    return true;
  },
  {
    message: 'Please indicate if students are charged fees',
    path: ['hasStudentFees']
  }
).refine(
  (data) => {
    if (data.hasTestPrep === true) {
      return data.isOutsideCompany !== undefined;
    }
    return true;
  },
  {
    message: 'Please indicate if services are provided by an outside company',
    path: ['isOutsideCompany']
  }
).refine(
  (data) => {
    if (data.hasTestPrep === true) {
      return data.hasInstructorCompensation !== undefined;
    }
    return true;
  },
  {
    message: 'Please indicate if instructors receive additional compensation',
    path: ['hasInstructorCompensation']
  }
).refine(
  (data) => {
    if (data.hasTestPrep === true) {
      return data.hasAccessToSecureMaterials !== undefined;
    }
    return true;
  },
  {
    message: 'Please indicate if test prep staff will have access to secure materials',
    path: ['hasAccessToSecureMaterials']
  }
);

// Schema for IAIStaff
const AIStaffSchema = z.object({
  totalStaff: z.string()
    .min(1, 'Total staff count is required')
    .refine(val => {
      const num = parseInt(val);
      return !isNaN(num) && num > 0;
    }, 'Total staff must be a positive number greater than 0'),
  hasRelativeEmployees: z.boolean(),
  relativeEmployeeCount: z.string().optional()
});

// Schema for IOrg
const OrgSchema = z.object({
  name: z.string()
    .min(1, 'Organization name is required')
    .max(255, 'Organization name cannot exceed 255 characters')
    .regex(
      /^[A-Za-z0-9 .&/:,()@#+!'\-]*$/,
      'Organization name can only contain letters, numbers, and these characters: .&/:,()@#+!\'-'
    ),
  email: EmailSchema,
  phone: z.string()
    .min(7, 'Phone number must be at least 7 and only numbers')
    .regex(/^\d+$/, 'Phone number must contain only numbers'),
  website: UrlWithTLDSchema,
  visitingAddress: z.any(), // We'll validate this separately with getAddressSchema
  shippingAddress: z.any().optional(), // Optional
  shippingPolicyException: z.string().optional()
});

// Schema for IAI
const AISchema = z.object({
  appliedLevel: z.enum(['1', '2']).optional()
});

// Base schema for IAIAppForm (non-conditional fields)
const baseAppFormSchema = z.object({
  isSchoolDistrictMember: z.boolean(),
  schoolDistricts: z.string().optional(),
  establishedDate: DateSchema,
  sharedOrgAddress: z.boolean(),
  sharedOrgAddressInfo: z.string().optional(),
  existingOrg: z.boolean(),
  existingOrgInfo: z.string().optional(),
  hasCollegeBoardCode: z.boolean(),
  collegeBoardCodes: z.string().optional(),
  mergerSchoolCodes: z.string().optional(),
  type: z.array(z.string()).min(1, 'At least one organization type must be selected'),
  typeOther: z.string().optional(),
  creditType: z.array(z.string()).min(1, 'At least one option must be selected'),
  creditTypeOther: z.string().optional(),
  academicDisciplines: z.array(z.string()).min(1, 'At least one option must be selected'),
  academicDisciplinesOther: z.string().optional(),
  teachingMethod: z.array(z.string()).min(1, 'At least one teaching method must be selected'),
  teachingMethodOther: z.string().optional(),
  onsiteStudents: OnsiteStudentsSchema,
  studentsEnrolled: EnrolledStudentsSchema,
  examsInformation: ExamsInformationSchema,
  accreditation: AccreditationSchema,
  submitterComments: z.string()
    .max(2000, 'Cannot exceed 2000 characters')
    .regex(textAreaPattern, textAreaPatternMessage)
    .optional(),
});

// Grade pairs for onsite vs enrolled cross-validation
const GRADE_PAIRS = [
  { onsite: 'zeroTaught', enrolled: 'zeroEnrolled', label: 'K' },
  { onsite: 'oneTaught', enrolled: 'oneEnrolled', label: '1st' },
  { onsite: 'twoTaught', enrolled: 'twoEnrolled', label: '2nd' },
  { onsite: 'threeTaught', enrolled: 'threeEnrolled', label: '3rd' },
  { onsite: 'fourTaught', enrolled: 'fourEnrolled', label: '4th' },
  { onsite: 'fiveTaught', enrolled: 'fiveEnrolled', label: '5th' },
  { onsite: 'sixTaught', enrolled: 'sixEnrolled', label: '6th' },
  { onsite: 'sevenTaught', enrolled: 'sevenEnrolled', label: '7th' },
  { onsite: 'eightTaught', enrolled: 'eightEnrolled', label: '8th' },
  { onsite: 'nineTaught', enrolled: 'nineEnrolled', label: '9th' },
  { onsite: 'tenTaught', enrolled: 'tenEnrolled', label: '10th' },
  { onsite: 'elevenTaught', enrolled: 'elevenEnrolled', label: '11th' },
  { onsite: 'twelveTaught', enrolled: 'twelveEnrolled', label: '12th' },
];

/**
 * Get AppForm Schema with conditional validation
 */
const getAppFormSchema = (appliedLevel: '1' | '2' | undefined, update: DeepPartial<IAIAppForm>) => {
  const baseSchema = baseAppFormSchema.extend({
    // Conditional fields based on boolean selections
    schoolDistricts: update.isSchoolDistrictMember ?
      requiredTextField('Please list the school district or school network') :
      optionalTextField(),

    sharedOrgAddressInfo: update.sharedOrgAddress === true ?
      requiredTextField('Please list the organization you share address with') :
      optionalTextField(),

    existingOrgInfo: update.existingOrg ?
      requiredTextField('Please provide the previously used name and/or address') :
      optionalTextField(),

    collegeBoardCodes: update.hasCollegeBoardCode ?
      z.string()
        .min(1, 'Required')
        .refine(
          (val) => {
            // Split by comma and trim whitespace
            const codes = val.split(',').map(code => code.trim());

            // Check if all codes are numeric and have max 6 digits
            return codes.every(code => {
              // Check if code is numeric
              if (!/^\d+$/.test(code)) return false;
              // Check if code has max 6 digits
              if (code.length > 6) return false;
              return true;
            });
          },
          'College Board codes must be comma-separated numbers with a maximum of 6 digits each'
        ) :
      z.string().optional(),

    mergerSchoolCodes: z.string().optional(),

    typeOther: (update.type && update.type.includes('Other')) ?
      requiredTextField('Please provide an explanation of your school type') :
      optionalTextField(),

    creditTypeOther: (update.creditType && update.creditType.includes('Other')) ?
      requiredTextField('Please explain the credit type') :
      optionalTextField(),

    academicDisciplinesOther: (update.academicDisciplines && update.academicDisciplines.includes('Other')) ?
      requiredTextField('Please list other academic disciplines') :
      optionalTextField(),

    teachingMethodOther: (update.teachingMethod && update.teachingMethod.includes('Other')) ?
      requiredTextField('Please explain the teaching method') :
      optionalTextField(),


    // New required principal fields validation
    principalName: requiredTextField('Principal or head of school name is required'),
    principalEmail: z.string()
      .min(1, 'Principal or head of school email is required')
      .regex(emailPattern, emailPatternMessage),
    principalWebpage: RequiredUrlWithTLDSchema,

  });
  if (appliedLevel === '2') {
    const examSecuritySchema = getExamSecuritySchema(update.examSecurity?.alternativeTestingAddress);
    return baseSchema.extend({
      studentSchedule: StudentScheduleSchema,
      aiTestPrep: AITestPrepSchema,
      aiStaff: AIStaffSchema.extend({
        relativeEmployeeCount: update.aiStaff?.hasRelativeEmployees === true ?
          z.string()
            .min(1, 'Relative employee count is required')
            .refine(val => {
              const num = parseInt(val);
              return !isNaN(num) && num > 0;
            }, 'Relative employee count must be a positive number greater than 0') :
          z.string().optional()
      }),
      examSecurity: examSecuritySchema
    }).superRefine((data, ctx) => {
      for (const { onsite, enrolled, label } of GRADE_PAIRS) {
        const onsiteVal = parseInt(data.onsiteStudents?.[onsite as keyof typeof data.onsiteStudents] || '');
        const enrolledVal = parseInt(data.studentsEnrolled?.[enrolled as keyof typeof data.studentsEnrolled] || '');
        if (!isNaN(onsiteVal) && !isNaN(enrolledVal) && onsiteVal > enrolledVal) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Grade ${label} taught on-site cannot exceed total students enrolled in grade ${label}`,
            path: ['onsiteStudents', onsite],
          });
        }
      }
    });
  }
  // getAddFormSchema has 2 distinct return statements, hence superRefine is called on the baseSchema in both cases to ensure cross-field validation runs regardless of applied level
  return baseSchema.superRefine((data, ctx) => {
    for (const { onsite, enrolled, label } of GRADE_PAIRS) {
      const onsiteVal = parseInt(data.onsiteStudents?.[onsite as keyof typeof data.onsiteStudents] || '');
      const enrolledVal = parseInt(data.studentsEnrolled?.[enrolled as keyof typeof data.studentsEnrolled] || '');
      if (!isNaN(onsiteVal) && !isNaN(enrolledVal) && onsiteVal > enrolledVal) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Grade ${label} taught on-site cannot exceed total students enrolled in grade ${label}`,
          path: ['onsiteStudents', onsite],
        });
      }
    }
  });
};

/**
 * Helper function to check if shipping address is populated
 * Checks if any of the core address fields have values
 */
function hasShippingAddress(shippingAddress: any): boolean {
  if (!shippingAddress) return false;

  // Check for US address fields
  const hasStreetAddress = shippingAddress.streetAddr1 && shippingAddress.streetAddr1.trim().length > 0;
  const hasCity = shippingAddress.city && shippingAddress.city.trim().length > 0;
  const hasState = shippingAddress.stateCd && shippingAddress.stateCd.trim().length > 0;
  const hasZip = shippingAddress.zip5 && shippingAddress.zip5.trim().length > 0;

  // Check for international address fields
  const hasProvince = shippingAddress.province && shippingAddress.province.trim().length > 0;
  const hasIntlPostal = shippingAddress.intlPostalCd && shippingAddress.intlPostalCd.trim().length > 0;

  // Consider shipping address "populated" if it has basic required fields
  // For US: streetAddr1, city, stateCd, or zip5
  // For International: streetAddr1, city, province, or intlPostalCd
  return hasStreetAddress || hasCity || hasState || hasZip || hasProvince || hasIntlPostal;
}

/**
 * Base schema for IAIData (all non-conditional fields)
 */
const baseSchema = z.object({
  ai: AISchema,
  org: OrgSchema,
  testingPlan: z.any().optional() // Testing plan has its own validation
});

/**
 * Validation Schema Generator
 * 
 * This function creates a schema that validates the IAIData structure
 * with conditional field validation based on user selections.
 */
export const getSchema = (update: DeepPartial<IAIData>) => {
  const { visitingAddress, shippingAddress, shippingPolicyException } = update.org || {};
  const VisitingAddressSchema = getAddressSchema(visitingAddress?.countryIsoCd);
  const ShippingAddressSchema = getAddressSchema(shippingAddress?.countryIsoCd);
  const appFormSchema = getAppFormSchema(update.ai?.appliedLevel, update.appForm || {});

  // Create conditional OrgSchema based on shipping address
  const conditionalOrgSchema = OrgSchema.extend({
    visitingAddress: VisitingAddressSchema,
    shippingAddress: ShippingAddressSchema.optional(),
    shippingPolicyException: z.string().optional()
  }).superRefine((data, ctx) => {
    // Check if shipping address is populated
    const hasShipping = hasShippingAddress(data.shippingAddress);

    if (hasShipping) {
      // Validate shippingPolicyException independently
      if (!data.shippingPolicyException || data.shippingPolicyException.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Policy exception explanation is required when shipping address is provided',
          path: ['shippingPolicyException']
        });
      }
    }
  });

  return baseSchema.extend({
    org: conditionalOrgSchema,
    appForm: appFormSchema
  });
};

/**
 * Validation function for AI Create form data
 * 
 * This validates the entire IAIData structure including all conditional fields.
 */
export const validate = (appliedLevel: '1' | '2', data: DeepPartial<IAIData>) => {
  // Validate applied level
  if (!appliedLevel || !['1', '2'].includes(appliedLevel)) {
    throw new ValidationError({
      _errors: ['Applied level must be either "1" or "2"']
    });
  }

  // Ensure AI data has the applied level
  const dataWithLevel = {
    ...data,
    ai: {
      ...data.ai,
      appliedLevel
    }
  };

  const schema = getSchema(dataWithLevel);

  try {
    const validated = schema.parse(dataWithLevel);
    return validated;
  } catch (error: any) {
    if (error instanceof z.ZodError) {
      const errors: IFieldErrors = error.format();
      throw new ValidationError(errors);
    }
    throw new Error(error?.message || 'Validation failed');
  }
};

/**
 * Validate just the AI portion (for step validation)
 */
export const validateAI = (appliedLevel: '1' | '2', orgData: DeepPartial<IOrg>, appFormData: DeepPartial<IAIAppForm>) => {
  if (!appliedLevel || !['1', '2'].includes(appliedLevel)) {
    throw new ValidationError({
      _errors: ['Applied level must be either "1" or "2"']
    });
  }

  const aiData: DeepPartial<IAIData> = {
    ai: { appliedLevel },
    org: orgData,
    appForm: appFormData
  };

  return validate(appliedLevel, aiData);
};